// 他参加者の「操作中（未確定）」のゴースト／プレビューを描画するレイヤ。
// - object.transient.transform: ドラッグ移動 / リサイズ / 回転中の枠を破線ゴーストで表示
// - pen.stroke.append: 描画中ペンストロークを折れ線で表示
//
// transient store（useCanvasTransientStore）を購読し、リアルタイムに追従する。
// 自分自身の操作分は描画しない（自分の画面では実 DOM が既に動いているため）。

import { useEffect, type CSSProperties } from "react";
import type { CanvasObject } from "@refinement-canvas/domain";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useCanvasViewersStore } from "./store/canvas-viewers-store";
import {
  useCanvasTransientStore,
  type RemotePenPreview,
  type RemoteTransformPreview,
} from "./store/canvas-transient-store";
import { CANVAS_HEIGHT_PX, CANVAS_WIDTH_PX } from "../../lib/canvas-coordinates";

interface Props {
  readonly currentUserId: string;
}

// 一定時間更新が無いゴーストは破棄する（操作終了の確定 op を取りこぼした場合の保険）。
const STALE_MS = 1500;
// 描画中ストロークのゴーストの太さ（ステージ座標 px、確定時の正確な太さは未受信のため概算）。
const PEN_PREVIEW_THICKNESS_PX = 4;
const PREVIEW_LAYER_Z_INDEX = Number.MAX_SAFE_INTEGER - 2;

export function CanvasRemotePreviewsLayer({ currentUserId }: Props): JSX.Element {
  const transforms = useCanvasTransientStore((s) => s.transforms);
  const penStrokes = useCanvasTransientStore((s) => s.penStrokes);
  const prune = useCanvasTransientStore((s) => s.prune);
  const objects = useCanvasDocStore((s) => s.objects);
  const viewers = useCanvasViewersStore((s) => s.viewers);
  const now = Date.now();

  // 操作終了後に残るゴーストを定期的に掃除する（確定 op での破棄を取りこぼした場合の保険）。
  useEffect(() => {
    const id = window.setInterval(() => prune(STALE_MS), 1000);
    return () => window.clearInterval(id);
  }, [prune]);

  return (
    <div
      data-testid="canvas-remote-previews-layer"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: PREVIEW_LAYER_Z_INDEX,
      }}
    >
      <svg
        width={CANVAS_WIDTH_PX}
        height={CANVAS_HEIGHT_PX}
        style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "visible" }}
      >
        {Object.entries(penStrokes).map(([strokeId, stroke]) => {
          if (stroke.userId === currentUserId) return null;
          if (now - stroke.updatedAt > STALE_MS) return null;
          const color = normalizeColor(viewers[stroke.userId]?.color);
          return <RemotePenStroke key={strokeId} color={color} stroke={stroke} />;
        })}
      </svg>

      {Object.entries(transforms).map(([objectId, preview]) => {
        if (preview.userId === currentUserId) return null;
        if (now - preview.updatedAt > STALE_MS) return null;
        const base = objects[objectId];
        if (!base) return null;
        const viewer = viewers[preview.userId];
        return (
          <RemoteTransformGhost
            key={objectId}
            base={base}
            color={normalizeColor(viewer?.color)}
            displayName={viewer?.displayName}
            preview={preview}
          />
        );
      })}
    </div>
  );
}

function RemotePenStroke({
  color,
  stroke,
}: {
  readonly color: string;
  readonly stroke: RemotePenPreview;
}): JSX.Element | null {
  if (stroke.points.length < 2) return null;
  const points = stroke.points.map((p) => `${p.x},${p.y}`).join(" ");
  return (
    <polyline
      data-testid={`remote-pen-${stroke.strokeId}`}
      points={points}
      fill="none"
      stroke={color}
      strokeWidth={PEN_PREVIEW_THICKNESS_PX}
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity={0.85}
    />
  );
}

function RemoteTransformGhost({
  base,
  color,
  displayName,
  preview,
}: {
  readonly base: CanvasObject;
  readonly color: string;
  readonly displayName: string | undefined;
  readonly preview: RemoteTransformPreview;
}): JSX.Element {
  // プレビューに含まれない辺は確定済みオブジェクトの値で補う。
  const x = preview.x ?? base.x;
  const y = preview.y ?? base.y;
  const w = preview.w ?? base.w;
  const h = preview.h ?? base.h;
  const rotation = preview.rotation ?? base.rotation ?? 0;

  const containerStyle: CSSProperties = {
    position: "absolute",
    left: `${x}px`,
    top: `${y}px`,
    width: `${w}px`,
    height: `${h}px`,
    pointerEvents: "none",
  };
  const frameStyle: CSSProperties = {
    position: "absolute",
    inset: 0,
    border: `2px dashed ${color}`,
    borderRadius: "8px",
    boxSizing: "border-box",
    background: withAlpha(color, 0.08),
    transform: rotation ? `rotate(${rotation}deg)` : undefined,
    transformOrigin: "center",
  };

  return (
    <div
      data-testid={`remote-transform-ghost-${preview.userId}-${base.id}`}
      style={containerStyle}
    >
      <div aria-hidden="true" style={frameStyle} />
      {displayName ? (
        <div
          style={{
            position: "absolute",
            top: "-22px",
            left: "0",
            padding: "1px 6px",
            background: color,
            color: "#ffffff",
            fontSize: "11px",
            fontWeight: 700,
            borderRadius: "4px",
            whiteSpace: "nowrap",
            boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
          }}
        >
          {displayName}
        </div>
      ) : null}
    </div>
  );
}

function normalizeColor(color: string | undefined): string {
  return color && color.trim().length > 0 ? color : "#1e63d4";
}

function withAlpha(color: string, alpha: number): string {
  if (!/^#[0-9a-f]{6}$/iu.test(color)) return `rgba(30, 99, 212, ${alpha})`;
  const red = Number.parseInt(color.slice(1, 3), 16);
  const green = Number.parseInt(color.slice(3, 5), 16);
  const blue = Number.parseInt(color.slice(5, 7), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}
